// frontend\src\components\Header.js
import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import "../style/Header.css";
import { FaBars } from "react-icons/fa";
import logoImage from "../assets/blasterprime.png";
import Notification from "./Notification-dropdown";
import Profile from "../pages/user/Profile";
import { fetchApi } from "../utils/api";
import notificationIcon from "../assets/icon/bell.png";
import notificationActiveIcon from "../assets/icon/bell-active.png";

const Header = ({ toggleSidebar }) => {
  const { logout, currentUser, refreshUserFromAPI } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [showNotifications, setShowNotifications] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [profileImageKey, setProfileImageKey] = useState(Date.now());
  const [imageUrl, setImageUrl] = useState(null);
  const refreshAttempted = useRef(false);
  const mountCount = useRef(0);

  const profileRef = useRef(null);
  const notificationRef = useRef(null);

  // 🔹 Log mount count
  useEffect(() => {
    mountCount.current += 1;
    console.log(`🔷 Header mounted (${mountCount.current} times)`);
  }, []);

  // 🔹 Debug: Log currentUser changes dengan detail
  useEffect(() => {
    console.log("👤 ========== HEADER CURRENTUSER CHANGED ==========");
    console.log("👤 currentUser:", currentUser);
    console.log("👤 currentUser?.profile_image:", currentUser?.profile_image);
    console.log("👤 currentUser?.id:", currentUser?.id);
    
    if (currentUser?.profile_image) {
      console.log("🖼️ Profile image URL found:", currentUser.profile_image);
      setImageUrl(currentUser.profile_image);
      setImageError(false);
      setProfileImageKey(Date.now());
      refreshAttempted.current = false;
      console.log("✅ Image URL set to:", currentUser.profile_image);
    } else {
      console.log("❌ No profile image in currentUser");
      setImageUrl(null);
      
      // Refresh hanya sekali dan hanya jika currentUser ada
      if (currentUser && currentUser.id && !refreshAttempted.current) {
        console.log("🔄 Attempting to refresh user data (first time only)...");
        refreshAttempted.current = true;
        refreshUserFromAPI();
      } else if (currentUser && currentUser.id && refreshAttempted.current) {
        console.log("⚠️ Refresh already attempted, skipping...");
      } else if (!currentUser) {
        console.log("⚠️ No currentUser, skipping refresh");
      }
    }
    console.log("==================================================");
  }, [currentUser, refreshUserFromAPI]);

  // 🔹 Log imageUrl changes
  useEffect(() => {
    console.log("📸 imageUrl state changed:", imageUrl);
  }, [imageUrl]);

  // 🔹 Log imageError changes
  useEffect(() => {
    console.log("⚠️ imageError state changed:", imageError);
  }, [imageError]);

  // 🔹 Log profileImageKey changes
  useEffect(() => {
    console.log("🔑 profileImageKey changed:", profileImageKey);
  }, [profileImageKey]);

  // 🔹 Tutup dropdown saat route berubah
  useEffect(() => {
    console.log("📍 Route changed to:", location.pathname);
    setShowProfile(false);
    setShowNotifications(false);
  }, [location.pathname]);

  // 🔹 Detect screen size changes
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // 🔹 Logout handler
  const handleLogout = () => {
    console.log("🚪 Logout clicked");
    logout();
    navigate("/login", { replace: true });
  };

  // 🔹 Toggle dropdowns
  const toggleNotificationDropdown = () => {
    console.log("🔔 Toggle notification dropdown");
    setShowNotifications((prev) => !prev);
    setShowProfile(false);
  };

  const toggleProfileDropdown = () => {
    console.log("👤 Toggle profile dropdown");
    setShowProfile((prev) => !prev);
    setShowNotifications(false);
  };

  // 🔹 Handle toggle sidebar with mobile support
  const handleToggleSidebar = () => {
    console.log("☰ Toggle sidebar");
    if (isMobile) {
      toggleSidebar();
      setSidebarOpen(!sidebarOpen);
      setShowNotifications(false);
      setShowProfile(false);
      
      if (!sidebarOpen) {
        document.body.style.overflow = 'hidden';
      } else {
        document.body.style.overflow = 'unset';
      }
    } else {
      toggleSidebar();
      setSidebarOpen(!sidebarOpen);
    }
  };

  // Cleanup effect for body scroll
  useEffect(() => {
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  // Listen for sidebar state changes from parent
  useEffect(() => {
    const handleSidebarChange = (e) => {
      if (e.detail) {
        setSidebarOpen(e.detail.isOpen);
      }
    };

    window.addEventListener('sidebarStateChange', handleSidebarChange);
    return () => window.removeEventListener('sidebarStateChange', handleSidebarChange);
  }, []);

  // 🔹 Refresh notification data
  const refreshNotifications = () => {
    setLoading(true);
    let endpoint = "/notifications";
    if (currentUser?.role === "admin") {
      endpoint = "/notifications/admin";
    }

    fetchApi(endpoint)
      .then((data) => {
        setNotifications(data);
        const unread = data.filter((notif) => !notif.is_read).length;
        setUnreadCount(unread);
      })
      .catch((err) => console.error("Error refresh notifications:", err))
      .finally(() => setLoading(false));
  };

  // 🔹 Fetch unread count periodically
  const fetchUnreadCount = () => {
    fetchApi("/notifications/unread-count")
      .then((data) => setUnreadCount(data.count))
      .catch((err) => console.error("Error fetching unread count:", err));
  };

  useEffect(() => {
    const intervalId = setInterval(() => {
      fetchUnreadCount();
    }, 30000);
    return () => clearInterval(intervalId);
  }, []);

  // 🔹 Handle click outside (close dropdown)
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setShowProfile(false);
      }
      if (notificationRef.current && !notificationRef.current.contains(event.target)) {
        setShowNotifications(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // 🔹 Auto-refresh notifications
  useEffect(() => {
    if (showNotifications) {
      refreshNotifications();
    } else {
      refreshNotifications();
      const intervalId = setInterval(() => {
        refreshNotifications();
      }, 30000);
      return () => clearInterval(intervalId);
    }
  }, [showNotifications]);

  // 🔹 Refresh header when profile updated
  useEffect(() => {
    const handleProfileUpdated = (event) => {
      console.log("📸 ========== PROFILE UPDATED EVENT ==========");
      console.log("📸 Event detail:", event.detail);
      console.log("📸 profile_image from event:", event.detail?.profile_image);
      setImageError(false);
      setProfileImageKey(Date.now());
      
      if (event.detail?.profile_image) {
        console.log("✅ Setting imageUrl from event:", event.detail.profile_image);
        setImageUrl(event.detail.profile_image);
        refreshAttempted.current = false;
      } else {
        console.log("⚠️ No profile_image in event detail");
      }
      console.log("==============================================");
    };

    window.addEventListener("profileUpdated", handleProfileUpdated);
    return () => window.removeEventListener("profileUpdated", handleProfileUpdated);
  }, []);

  // 🔹 Storage change listener
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'user') {
        console.log("📦 ========== STORAGE CHANGED ==========");
        console.log("📦 New user data:", e.newValue);
        setImageError(false);
        setProfileImageKey(Date.now());
        try {
          const updatedUser = JSON.parse(e.newValue);
          console.log("📦 Parsed user:", updatedUser);
          if (updatedUser?.profile_image) {
            console.log("✅ Setting imageUrl from storage:", updatedUser.profile_image);
            setImageUrl(updatedUser.profile_image);
            refreshAttempted.current = false;
          } else {
            console.log("⚠️ No profile_image in storage user");
          }
        } catch (err) {
          console.error("Error parsing user from storage:", err);
        }
        console.log("========================================");
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // Fungsi untuk mendapatkan inisial nama
  const getInitials = () => {
    if (currentUser?.name) {
      return currentUser.name.charAt(0).toUpperCase();
    }
    if (currentUser?.username) {
      return currentUser.username.charAt(0).toUpperCase();
    }
    if (currentUser?.email) {
      return currentUser.email.charAt(0).toUpperCase();
    }
    return "U";
  };

  return (
    <header className="topnav">
      <div className="left">
        <button 
          className="menu-toggle" 
          onClick={handleToggleSidebar}
          aria-label="Toggle menu"
        >
          <FaBars />
        </button>
        <img src={logoImage} alt="BLASTERC" className="logo-img" />
      </div>

      <div className="right">
        {/* 🔔 Notifikasi */}
        <div className="notification-wrapper" ref={notificationRef}>
          <button 
            className="notification-icon" 
            onClick={toggleNotificationDropdown}
            title="Notifications"
          >
            <img
              src={showNotifications ? notificationActiveIcon : notificationIcon}
              alt="Notifications"
              className="notification-icon-img"
            />
            
            {unreadCount > 0 && (
              <span className="notification-badge">
                {unreadCount > 99 ? "99+" : unreadCount}
              </span>
            )}
          </button>
          {showNotifications && (
            <div className="notification-dropdown">
              <Notification
                notifications={notifications}
                loading={loading}
                refreshNotifications={refreshNotifications}
                onClose={() => setShowNotifications(false)}
              />
            </div>
          )}
        </div>

        {/* 👤 Profile Dropdown */}
        <div className="profile-wrapper" ref={profileRef}>
          <div className="profile-pic-container" onClick={toggleProfileDropdown}>
            {/* Debug info */}
            <div style={{ display: 'none' }}>
              imageUrl: {imageUrl}, imageError: {String(imageError)}
            </div>
            
            {/* Coba tampilkan gambar jika ada dan tidak error */}
            {imageUrl && !imageError ? (
              <img
                key={profileImageKey}
                src={imageUrl}
                alt="Profile"
                className="profile-pic"
                onError={(e) => {
                  console.error("❌ Gagal memuat foto profil:", imageUrl);
                  setImageError(true);
                  e.target.style.display = "none";
                }}
                onLoad={() => {
                  console.log("✅ Foto profil berhasil dimuat:", imageUrl);
                  setImageError(false);
                }}
              />
            ) : (
              console.log("🔍 Rendering fallback - imageUrl:", imageUrl, "imageError:", imageError)
            )}
            
            {/* Fallback - tampilkan inisial */}
            <div 
              className="profile-fallback"
              style={{
                display: (imageUrl && !imageError) ? 'none' : 'flex',
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                backgroundColor: '#0044cc',
                color: 'white',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '18px',
                fontWeight: 'bold',
                textTransform: 'uppercase'
              }}
            >
              {getInitials()}
            </div>
          </div>

          {/* Dropdown Content */}
          {showProfile && (
            <div className="profile-dropdown">
              <Profile 
                logout={handleLogout} 
                onMenuClick={() => setShowProfile(false)} 
                onClose={() => setShowProfile(false)}
              />
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;